import React, { useEffect } from 'react';
import { Bell, X, MoreHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AccountCard } from './AccountCard';
import LanguageSwitcher from '../LanguageSwitcher';
import { useProfileStore } from '../../stores/profileStore';
import { useTranslation } from 'react-i18next';
import { ThemeToggleButton } from '../common/ThemeToggleButton';

interface AdminMobileNavigationProps {
  isOpen: boolean;
  onToggle: (forceClose?: boolean) => void;
  onSignOut: () => void;
  notifications: number;
}

export const AdminMobileNavigation: React.FC<AdminMobileNavigationProps> = ({
  isOpen,
  onToggle,
  onSignOut,
  notifications,
}) => {
  const { profile } = useProfileStore();
  const { t } = useTranslation('common');

  // Bloque le scroll du body quand le menu est ouvert
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Menu principal quand connecté
  const renderLoggedInMainMenu = () => (
    <section className="w-full mt-6">
      <div className="text-sm text-white mb-2 font-bold">{t('mobileNavigation.mainMenu')}</div>
      <div className="bg-transparent p-6 flex flex-col gap-2 border border-white rounded-3xl">
        <div className="w-full flex justify-center mt-6">
          <LanguageSwitcher className="!text-white" />
        </div>
      </div>
    </section>
  );

  // Menu quand déconnecté
  const renderLoggedOutMainMenu = () => (
    <div className="w-full flex justify-center pb-8 mt-10">
      <LanguageSwitcher className="!text-white" />
    </div>
  );

  return (
    <div>
      {/* Bouton d’ouverture du menu */}
      <button
        onClick={() => onToggle()}
        className="md:hidden flex items-center justify-center w-10 h-10 text-gray-800"
        aria-label="Ouvrir le menu"
      >
        <MoreHorizontal />
      </button>

      {/* Overlay du menu mobile */}
      <div
        className={`fixed inset-0 z-[9999] bg-[#2D3648] flex flex-col items-center justify-between pt-6 pb-14 px-4 transition-transform duration-300 ease-in-out overflow-y-auto max-h-screen ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* En-tête du menu mobile */}
        <div className="flex items-center justify-between w-full">
          <h2 className="text-lg font-semibold text-white">Menu</h2>
          <button
            onClick={() => onToggle(true)}
            className="text-white p-2 rounded-lg hover:bg-white/10 transition"
            aria-label="Fermer le menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Notification + thème */}
        {profile && (
          <div className="flex flex-col items-center justify-center w-full mt-6 mb-4 gap-6">
            <Link
              to="/admin/notifications"
              className="relative w-10 h-10 bg-[#E1E9F0] rounded-full flex items-center justify-center"
              onClick={() => onToggle(true)}
            >
              <Bell className="w-5 h-5 text-secondary" />
              {notifications > 0 && (
                <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full px-1.5 py-0.5">
                  {notifications}
                </span>
              )}
            </Link>
            <ThemeToggleButton />
          </div>
        )}

        {/* Compte utilisateur */}
        {profile && (
          <div className="w-full mt-8 flex flex-col gap-4">
            <AccountCard
              firstName={profile.first_name}
              lastName={profile.last_name}
              avatarUrl={profile.avatar_url}
              onNavigate={onToggle}
              onLogout={onSignOut}
            />
          </div>
        )}

        {/* Menu principal */}
        {profile ? renderLoggedInMainMenu() : renderLoggedOutMainMenu()}
      </div>
    </div>
  );
};
